// idempotencia.js — A chave que impede o mesmo carrinho de virar dois pedidos.
//
// POR QUE ESTE ARQUIVO EXISTE:
// o pedido nasce no servidor ANTES do pagamento, e cada POST deixa uma linha
// para trás. Foi assim que um carrinho de 3 livros virou os pedidos #511, #512
// e #515 — nove livros para quem "pediu apenas 3". Trava de botão no front não
// resolve isso sozinha: ela não alcança o caso em que a requisição REALMENTE
// saiu duas vezes (timeout, aba recarregada, cliente voltando do gateway).
//
// A defesa que alcança é uma chave: a mesma string vai em toda tentativa do
// mesmo carrinho, e o servidor devolve o pedido que já existe em vez de criar
// outro. Quem decide se a chave continua valendo é o CARRINHO, não o tempo:
// enquanto ele for o mesmo, a chave é a mesma; mudou um item, é outra compra e
// a chave tem de ser nova — senão a segunda compra do cliente cairia dentro do
// pedido da primeira.
//
// A chave mora no `sessionStorage` (e não no `localStorage`) de propósito:
// ela precisa sobreviver a um F5 no meio do checkout — que é justamente o que
// o cliente faz quando a página parece travada — e precisa morrer quando a aba
// morre. `localStorage` a manteria viva por semanas, e uma recompra do mesmo
// carrinho no mês seguinte seria respondida com o pedido antigo.
//
// O QUE A ASSINATURA NÃO COBRE: só ids, formatos e quantidades entram nela.
// Ligar ou desligar os Insigne Coins não gera chave nova, então quem tentar
// pagar de novo com desconto diferente recebe de volta o pedido pendente já
// criado, com o valor da primeira tentativa. É o preço aceito aqui: um pedido
// com desconto errado o cliente cancela em um clique; dois pedidos cobrados de
// verdade viram reembolso manual, que não existe em rota nenhuma da API.

const CHAVE_STORAGE = "checkout_idempotencia";

// Espelho em memória. O `sessionStorage` do Safari em navegação privada LANÇA
// ao escrever (não devolve erro: lança), e o mesmo acontece com bloqueio de
// cookies de terceiros dentro de iframe. Sem este espelho, a exceção subiria e
// mataria a compra — trocaríamos um pedido duplicado por zero pedidos. Com ele
// a chave ainda funciona enquanto a página não recarregar, que é o cenário do
// clique repetido.
let emMemoria = null;

/**
 * UUID v4. `crypto.randomUUID` existe em todo navegador atual, mas só em
 * contexto seguro (https ou localhost) — em http puro ele simplesmente não
 * está lá. Daí o fallback por `getRandomValues`, e só depois o `Math.random`,
 * que não é criptográfico e está aqui apenas para nunca faltar chave.
 *
 * O formato de saída cabe no que o servidor valida: 8 a 64 caracteres de
 * [A-Za-z0-9-].
 */
const gerarChave = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const b = crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40; // versão 4
      b[8] = (b[8] & 0x3f) | 0x80; // variante RFC 4122
      const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {}
  return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * A identidade do carrinho, em uma linha.
 *
 * Ordenada porque a ORDEM dos itens não é parte da compra: adicionar A depois B
 * ou B depois A é o mesmo pedido, e sem o `sort` reordenar a lista geraria uma
 * chave nova — ou seja, um segundo pedido — sem que nada tivesse mudado.
 */
const assinaturaDoCarrinho = (carrinho) =>
  (Array.isArray(carrinho) ? carrinho : [])
    .map((i) => `${i?.id}:${i?.formato || "ebook"}:${i?.quantidade || 0}`)
    .sort()
    .join("|");

const lerGuardado = () => {
  try {
    const cru = sessionStorage.getItem(CHAVE_STORAGE);
    if (!cru) return emMemoria;
    const dado = JSON.parse(cru);
    return dado && dado.chave && typeof dado.assinatura === "string" ? dado : emMemoria;
  } catch {
    return emMemoria;
  }
};

const guardar = (dado) => {
  emMemoria = dado;
  try {
    sessionStorage.setItem(CHAVE_STORAGE, JSON.stringify(dado));
  } catch {}
};

/**
 * A MESMA chave enquanto o carrinho não mudar; uma NOVA assim que ele mudar.
 *
 * É esta função que dá sentido às duas metades do problema: o cliente que
 * recarrega e tenta outra vez o mesmo carrinho reaproveita o pedido em vez de
 * criar outro; o cliente que muda o carrinho e compra de novo cria um pedido de
 * verdade, e não recebe de volta o anterior.
 */
export const chaveParaCarrinho = (carrinho) => {
  const assinatura = assinaturaDoCarrinho(carrinho);
  const guardado = lerGuardado();
  if (guardado && guardado.assinatura === assinatura) return guardado.chave;
  const novo = { chave: gerarChave(), assinatura };
  guardar(novo);
  return novo.chave;
};

/**
 * Some com a chave. Chamada no sucesso (o pedido existe: manter a chave faria a
 * próxima compra do mesmo carrinho ser respondida com o pedido velho) e sempre
 * que o carrinho muda.
 *
 * NÃO chamar quando a resposta do POST não chegou: ali a chave é justamente o
 * que protege a retentativa.
 */
export const descartarChave = () => {
  emMemoria = null;
  try {
    sessionStorage.removeItem(CHAVE_STORAGE);
  } catch {}
};
